import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getOverviewStats,
  getPageAnalytics,
  getAllFormsAnalytics,
  getTrafficSources,
} from '../services/analyticsService';
import { createTodo } from '../services/todoService';
import { CreateTodoModal } from '../components/todos';
import { Toast } from '../components/ui';
import type { PageAnalytics, FormAnalytics, TrafficSourceData, OverviewStats, ReferrerSource } from '../types/analytics';
import type { CreateTodoInput } from '../types/todo';
import {
  BarChart3,
  Eye,
  Send,
  Users,
  Globe,
  FileText,
  Instagram,
  MapPin,
  CheckSquare,
} from 'lucide-react';
import { logger } from '../lib/logger';

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
}

const SOURCE_CONFIG: Record<ReferrerSource, { label: string; icon: React.FC<{ className?: string }>; color: string }> = {
  instagram: { label: 'Instagram', icon: Instagram, color: 'bg-pink-100 text-pink-600' },
  facebook: { label: 'Facebook', icon: FacebookIcon, color: 'bg-blue-100 text-blue-600' },
  tiktok: { label: 'TikTok', icon: TikTokIcon, color: 'bg-gray-900 text-white' },
  direct: { label: 'Direkt', icon: MapPin, color: 'bg-emerald-100 text-emerald-600' },
  other: { label: 'Andere', icon: Globe, color: 'bg-gray-100 text-gray-600' },
};

type TimePeriod = 7 | 14 | 30 | 90 | 365;

const TIME_PERIOD_LABELS: Record<TimePeriod, string> = {
  7: 'Letzte 7 Tage',
  14: 'Letzte 14 Tage',
  30: 'Letzte 30 Tage',
  90: 'Letzte 90 Tage',
  365: 'Letztes Jahr',
};

export function Analytics() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(7);
  const [overview, setOverview] = useState<OverviewStats>({
    totalPageViews: 0,
    totalFormSubmissions: 0,
    totalLeads: 0,
  });
  const [businessAnalytics, setBusinessAnalytics] = useState<PageAnalytics | null>(null);
  const [productsAnalytics, setProductsAnalytics] = useState<PageAnalytics | null>(null);
  const [formAnalytics, setFormAnalytics] = useState<FormAnalytics[]>([]);
  const [trafficSources, setTrafficSources] = useState<TrafficSourceData[]>([]);
  const [isTodoModalOpen, setIsTodoModalOpen] = useState(false);
  const [showTodoToast, setShowTodoToast] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadAnalytics();
    }
  }, [user?.id, selectedPeriod]);

  const loadAnalytics = async () => {
    if (!user?.id) return;
    setIsLoading(true);

    try {
      const [
        overviewData,
        businessData,
        productsData,
        formsData,
        trafficData,
      ] = await Promise.all([
        getOverviewStats(user.id, selectedPeriod),
        getPageAnalytics(user.id, 'BUSINESS', selectedPeriod),
        getPageAnalytics(user.id, 'PRODUCTS', selectedPeriod),
        getAllFormsAnalytics(user.id, selectedPeriod),
        getTrafficSources(user.id, selectedPeriod),
      ]);

      setOverview(overviewData);
      setBusinessAnalytics(businessData);
      setProductsAnalytics(productsData);
      setFormAnalytics(formsData);
      setTrafficSources(trafficData);
    } catch (error) {
      logger.error('Error loading analytics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTodo = async (input: CreateTodoInput) => {
    if (!user) return;
    await createTodo(user.id, input);
    setIsTodoModalOpen(false);
    setShowTodoToast(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalTraffic = trafficSources.reduce((sum, s) => sum + s.pageViews, 0);

  return (
    <div className="space-y-6">
      {showTodoToast && (
        <Toast
          message="Todo erfolgreich erstellt!"
          linkText="Zu den Todos"
          linkTo="/todos"
          onClose={() => setShowTodoToast(false)}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] dark:text-white">Analytics</h1>
          <p className="text-[#6B6B6B] dark:text-slate-400 mt-1">
            Detaillierte Statistiken - {TIME_PERIOD_LABELS[selectedPeriod]}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(Number(e.target.value) as TimePeriod)}
            className="px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-800 dark:text-white text-sm font-medium"
          >
            {(Object.keys(TIME_PERIOD_LABELS) as unknown as TimePeriod[]).map((period) => (
              <option key={period} value={period}>
                {TIME_PERIOD_LABELS[period]}
              </option>
            ))}
          </select>
          {user && (
            <button
              onClick={() => setIsTodoModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-white rounded-xl transition-colors font-medium"
            >
              Todo erstellen
              <CheckSquare className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <Eye className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-[#1A1A1A] dark:text-white">{overview.totalPageViews}</p>
          <p className="text-sm text-[#6B6B6B] dark:text-slate-400 mt-1">Seitenaufrufe</p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
              <Send className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-[#1A1A1A] dark:text-white">{overview.totalFormSubmissions}</p>
          <p className="text-sm text-[#6B6B6B] dark:text-slate-400 mt-1">Formular-Einsendungen</p>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-cyan-50 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-cyan-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-[#1A1A1A] dark:text-white">{overview.totalLeads}</p>
          <p className="text-sm text-[#6B6B6B] dark:text-slate-400 mt-1">Neue Leads</p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center">
            <Globe className="w-5 h-5 text-teal-600" />
          </div>
          <h2 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">Seiten-Performance</h2>
        </div>
        {/* Desktop Tabelle */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider border-b border-gray-100 dark:border-slate-700">
                <th className="pb-3 pr-4">Seite</th>
                <th className="pb-3 pr-4 text-center">Aufrufe</th>
                <th className="pb-3 pr-4 text-center">Einsendungen</th>
                <th className="pb-3 pr-4 text-center">Conversion</th>
                <th className="pb-3 text-center">Top-Quelle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
              {businessAnalytics && (
                <tr className="text-sm">
                  <td className="py-4 pr-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                        <Globe className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="font-medium text-[#1A1A1A] dark:text-white">Business Page</span>
                    </div>
                  </td>
                  <td className="py-4 pr-4 text-center text-[#6B6B6B] dark:text-slate-400">{businessAnalytics.pageViews}</td>
                  <td className="py-4 pr-4 text-center text-[#6B6B6B] dark:text-slate-400">{businessAnalytics.submissions}</td>
                  <td className="py-4 pr-4 text-center">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                      {businessAnalytics.conversionRate}%
                    </span>
                  </td>
                  <td className="py-4 text-center">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${SOURCE_CONFIG[businessAnalytics.topReferrer].color}`}>
                      {SOURCE_CONFIG[businessAnalytics.topReferrer].label}
                    </span>
                  </td>
                </tr>
              )}
              {productsAnalytics && (
                <tr className="text-sm">
                  <td className="py-4 pr-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-violet-50 rounded-lg flex items-center justify-center">
                        <Globe className="w-4 h-4 text-violet-600" />
                      </div>
                      <span className="font-medium text-[#1A1A1A] dark:text-white">Product Page</span>
                    </div>
                  </td>
                  <td className="py-4 pr-4 text-center text-[#6B6B6B] dark:text-slate-400">{productsAnalytics.pageViews}</td>
                  <td className="py-4 pr-4 text-center text-[#6B6B6B] dark:text-slate-400">{productsAnalytics.submissions}</td>
                  <td className="py-4 pr-4 text-center">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                      {productsAnalytics.conversionRate}%
                    </span>
                  </td>
                  <td className="py-4 text-center">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${SOURCE_CONFIG[productsAnalytics.topReferrer].color}`}>
                      {SOURCE_CONFIG[productsAnalytics.topReferrer].label}
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile Karten */}
        <div className="md:hidden space-y-3">
          {businessAnalytics && (
            <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <Globe className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="font-medium text-[#1A1A1A] dark:text-white">Business Page</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="text-center">
                  <p className="text-lg font-semibold text-[#1A1A1A] dark:text-white">{businessAnalytics.pageViews}</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Aufrufe</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-[#1A1A1A] dark:text-white">{businessAnalytics.submissions}</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Einsendungen</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-emerald-600">{businessAnalytics.conversionRate}%</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Conversion</p>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-slate-600">
                <span className="text-xs text-[#6B6B6B] dark:text-slate-400">Top-Quelle</span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${SOURCE_CONFIG[businessAnalytics.topReferrer].color}`}>
                  {SOURCE_CONFIG[businessAnalytics.topReferrer].label}
                </span>
              </div>
            </div>
          )}
          {productsAnalytics && (
            <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-violet-50 rounded-lg flex items-center justify-center">
                  <Globe className="w-4 h-4 text-violet-600" />
                </div>
                <span className="font-medium text-[#1A1A1A] dark:text-white">Product Page</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="text-center">
                  <p className="text-lg font-semibold text-[#1A1A1A] dark:text-white">{productsAnalytics.pageViews}</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Aufrufe</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-[#1A1A1A] dark:text-white">{productsAnalytics.submissions}</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Einsendungen</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-emerald-600">{productsAnalytics.conversionRate}%</p>
                  <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Conversion</p>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-slate-600">
                <span className="text-xs text-[#6B6B6B] dark:text-slate-400">Top-Quelle</span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${SOURCE_CONFIG[productsAnalytics.topReferrer].color}`}>
                  {SOURCE_CONFIG[productsAnalytics.topReferrer].label}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 sm:p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center">
            <FileText className="w-5 h-5 text-rose-600" />
          </div>
          <h2 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">Formular-Performance</h2>
        </div>
        {formAnalytics.length > 0 ? (
          <>
            {/* Desktop Tabelle */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs font-medium text-[#6B6B6B] dark:text-slate-400 uppercase tracking-wider border-b border-gray-100 dark:border-slate-700">
                    <th className="pb-3 pr-4">Formular</th>
                    <th className="pb-3 pr-4 text-center">Aufrufe</th>
                    <th className="pb-3 pr-4 text-center">Starts</th>
                    <th className="pb-3 pr-4 text-center">Einsendungen</th>
                    <th className="pb-3 pr-4 text-center">View → Submit</th>
                    <th className="pb-3 text-center">Start → Submit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-slate-700">
                  {formAnalytics.map((form) => (
                    <tr key={form.formId} className="text-sm">
                      <td className="py-4 pr-4 font-medium text-[#1A1A1A] dark:text-white">{form.formName}</td>
                      <td className="py-4 pr-4 text-center text-[#6B6B6B] dark:text-slate-400">{form.views}</td>
                      <td className="py-4 pr-4 text-center text-[#6B6B6B] dark:text-slate-400">{form.starts}</td>
                      <td className="py-4 pr-4 text-center text-[#6B6B6B] dark:text-slate-400">{form.submissions}</td>
                      <td className="py-4 pr-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {form.viewToSubmitConversion}%
                        </span>
                      </td>
                      <td className="py-4 text-center">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          {form.startToSubmitConversion}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile Karten */}
            <div className="md:hidden space-y-3">
              {formAnalytics.map((form) => (
                <div key={form.formId} className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4">
                  <p className="font-medium text-[#1A1A1A] dark:text-white mb-3">{form.formName}</p>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="text-center">
                      <p className="text-lg font-semibold text-[#1A1A1A] dark:text-white">{form.views}</p>
                      <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Aufrufe</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-[#1A1A1A] dark:text-white">{form.starts}</p>
                      <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Starts</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-[#1A1A1A] dark:text-white">{form.submissions}</p>
                      <p className="text-xs text-[#6B6B6B] dark:text-slate-400">Einsendungen</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-slate-600">
                    <div className="text-center flex-1">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {form.viewToSubmitConversion}%
                      </span>
                      <p className="text-xs text-[#6B6B6B] dark:text-slate-400 mt-1">View → Submit</p>
                    </div>
                    <div className="text-center flex-1">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        {form.startToSubmitConversion}%
                      </span>
                      <p className="text-xs text-[#6B6B6B] dark:text-slate-400 mt-1">Start → Submit</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-[#6B6B6B] dark:text-slate-400 text-center py-4">
            Noch keine Formulare erstellt
          </p>
        )}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-amber-600" />
          </div>
          <h2 className="text-lg font-semibold text-[#1A1A1A] dark:text-white">Traffic-Quellen</h2>
        </div>
        {totalTraffic > 0 ? (
          <div className="space-y-4">
            {trafficSources.map((source) => {
              const config = SOURCE_CONFIG[source.source];
              const percentage = totalTraffic > 0 ? Math.round((source.pageViews / totalTraffic) * 100) : 0;
              const Icon = config.icon;
              return (
                <div key={source.source} className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-[#1A1A1A] dark:text-white">{config.label}</span>
                      <span className="text-sm text-[#6B6B6B] dark:text-slate-400">{source.pageViews} Aufrufe ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 dark:bg-slate-600 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-teal-400 to-teal-500 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-[#6B6B6B] dark:text-slate-400 text-center py-4">
            Noch keine Traffic-Daten vorhanden
          </p>
        )}
      </div>

      {/* Todo Modal */}
      {user && (
        <CreateTodoModal
          isOpen={isTodoModalOpen}
          onClose={() => setIsTodoModalOpen(false)}
          onSubmit={handleCreateTodo}
          defaultTitle="Analytics Review"
          defaultDescription="Conversion Rate analysieren und Marketing-Strategie anpassen"
          tag="general"
        />
      )}
    </div>
  );
}
